import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

const EmployeeManager = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [limit, setLimit] = useState(null);
  const [used, setUsed] = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!token) return;
    try {
      const data = await api.listEmployees(token);
      setEmployees(data);
      const meta = await api.employeeUsage(token);
      setLimit(meta.limit);
      setUsed(meta.used);
    } catch (e) {
      toast({ title: t("employees.loadError") });
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createEmployee(token, { email, password });
      setEmail("");
      setPassword("");
      load();
      toast({ title: t("employees.createSuccess") });
    } catch (e) {
      toast({ title: t("employees.createError"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(t("employees.confirmDelete"))) return;
    try {
      await api.deleteEmployee(token, id);
      load();
    } catch (e) {
      toast({ title: t("employees.deleteError"), variant: "destructive" });
    }
  };

  if (!user || user.role !== "ORG_OWNER")
    return <div className="p-6 text-sm">{t("employees.noPermission")}</div>;

  return (
    <div className="min-h-screen bg-muted p-6 space-y-6">
      <h1 className="text-2xl font-semibold">{t("employees.title")}</h1>
      <div className="text-xs text-muted-foreground">
        {limit !== null && used !== null && (
          <div className="flex items-center gap-2">
            <span>
              {used}/{limit}
            </span>
            <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
              <div
                className="h-full bg-blue-500"
                style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t("employees.limitAdjust")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const v = parseInt(newLimit);
              if (!v) return;
              try {
                await api.updateEmployeeLimit(token, v);
                toast({ title: t("employees.limitUpdated") });
                setNewLimit("");
                load();
              } catch (e) {
                toast({
                  title: t("employees.updateFailed"),
                  variant: "destructive",
                });
              }
            }}
            className="flex gap-2"
          >
            <input
              type="number"
              className="border rounded px-2 py-1 text-sm"
              placeholder={t("employees.placeholderCurrent", { limit })}
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
            />
            <Button type="submit">{t("employees.set")}</Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2">
            {t("employees.ownerReduceNote")}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t("employees.newEmployee")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="flex flex-col sm:flex-row gap-2">
            <input
              className="border rounded px-2 py-1 text-sm flex-1"
              placeholder={t("employees.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              className="border rounded px-2 py-1 text-sm"
              placeholder={t("employees.password")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" disabled={loading}>
              {loading ? "..." : t("employees.create")}
            </Button>
          </form>
        </CardContent>
      </Card>
      <div className="grid gap-3">
        {employees.map((e) => (
          <Card
            key={e.id}
            className="text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between"
          >
            <CardContent className="pt-4 flex-1">
              <div>{e.email}</div>
              <div className="text-xs text-muted-foreground">
                {new Date(e.createdAt).toLocaleString()}
              </div>
            </CardContent>
            <div className="p-4 pt-0 sm:pt-4 flex gap-2 justify-end">
              <Button variant="destructive" onClick={() => remove(e.id)}>
                {t("employees.delete")}
              </Button>
            </div>
          </Card>
        ))}
        {employees.length === 0 && (
          <div className="text-xs text-muted-foreground">{t("employees.none")}</div>
        )}
      </div>
    </div>
  );
};

export default EmployeeManager;
